import {
    Box,
    Tooltip,
    Typography,
    alpha,
    useTheme,
    type TooltipProps,
} from '@mui/material';
import React from 'react';
import { PetSeriesMetadata } from '~/domain/patient/petSuv';
import {
    LabelText,
    LabelValueBox,
    ValueText,
} from '~/components/Fields/TooltipLabeledValue';
import { formatDicomTime } from '~/domain/dicom/utils';

const SuvConversionTooltipContent = ({
    metadata,
}: {
    metadata: PetSeriesMetadata;
}) => {
    const doseMBq = metadata.radionuclideTotalDose
        ? `${(metadata.radionuclideTotalDose / 1e6).toFixed(1)} MBq`
        : '—';
    const halfLife = metadata.radionuclideHalfLife
        ? `${metadata.radionuclideHalfLife.toFixed(1)} s`
        : '—';
    const injTime = metadata.injectionDateTime
        ? `${formatDicomTime(metadata.injectionDateTime)} (hh:mm:ss)`
        : '—';
    const scanTime = metadata.scanDateTime
        ? `${formatDicomTime(metadata.scanDateTime)} (hh:mm:ss)`
        : '—';
    const weight = metadata.patientWeightKg
        ? `${metadata.patientWeightKg.toFixed(1)} kg`
        : '—';

    return (
        <Box whiteSpace="nowrap" overflow="hidden">
            <Typography fontSize={11} fontWeight="bold" sx={{ mb: 0.5 }}>
                SUVbw conversion parameters
            </Typography>
            <LabelValueBox>
                <LabelText>Patient Weight:</LabelText>
                <ValueText>{weight}</ValueText>
            </LabelValueBox>
            <LabelValueBox>
                <LabelText>Injected Dose:</LabelText>
                <ValueText>{doseMBq}</ValueText>
            </LabelValueBox>
            <LabelValueBox>
                <LabelText>Half-life:</LabelText>
                <ValueText>{halfLife}</ValueText>
            </LabelValueBox>
            <LabelValueBox>
                <LabelText>Injection Time:</LabelText>
                <ValueText>{injTime}</ValueText>
            </LabelValueBox>
            <LabelValueBox>
                <LabelText>Scan Time:</LabelText>
                <ValueText>{scanTime}</ValueText>
            </LabelValueBox>
        </Box>
    );
};

/**
 * The unit reads in the type of the label around it, and the pointer says
 * that hovering it explains it.
 */
const UNIT_SX = { font: 'inherit', cursor: 'help' } as const;

interface SuvUnitTooltipProps {
    metadata: PetSeriesMetadata | null;
    children: React.ReactNode;
    placement?: TooltipProps['placement'];
}

export const SuvUnitTooltip = ({
    metadata,
    children,
    placement = 'top',
}: SuvUnitTooltipProps) => {
    const theme = useTheme();
    const tooltipBg = alpha(theme.palette.background.default, 0.95);

    if (!metadata) {
        return <>{children}</>;
    }

    return (
        <Tooltip
            title={<SuvConversionTooltipContent metadata={metadata} />}
            placement={placement}
            arrow
            slotProps={{
                tooltip: {
                    sx: {
                        bgcolor: tooltipBg,
                        '& .MuiTooltip-arrow': {
                            color: tooltipBg,
                        },
                    },
                },
            }}
        >
            <Typography component="span" sx={UNIT_SX}>
                {children}
            </Typography>
        </Tooltip>
    );
};

export default SuvUnitTooltip;
